import { useState, useEffect, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { SALE_STATUS_LABELS, PAYMENT_FORMS } from "@/utils/constants";
import { useCanSeeBleedPaymentForm } from "@/hooks/useCanSeeBleedPaymentForm";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import { Mask } from "@/utils/mask";

const parseCurrencyToNumber = (value: string): number => {
  const cleanValue = value.replace(/[^\d]/g, "");
  return cleanValue ? parseInt(cleanValue, 10) / 100 : 0;
};

const formatNumberToCurrency = (value: number | undefined): string => {
  if (!value) return "";
  return Mask.formatCurrency(value);
};

export interface SalesFilters {
  sale_id?: string;
  unity_id?: string[];
  client_id?: string;
  client_name?: string;
  sale_type_id?: string;
  payment_form_id?: string[];
  per_page?: number;
  process_step_sale_id?: string;
  seller_id?: string[];
  bank_account_id?: string;
  sale_status_id?: string[];
  lottery_id?: string[];
  contest_id?: string[];
  client_origin_id?: string[];
  start_date?: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  order_by?: string;
  order_direction?: string;
  date_period?: "today" | "yesterday" | "thisWeek" | "thisMonth" | "last7days" | "custom";
  product_classification_id?: string[];
  product_name?: string;
  min_total?: number;
  max_total?: number;
  is_new_client?: string;
  /** Fila de pré-vendas: status da pré-venda. */
  pre_sale_status_id?: string[];
}

interface SalesFiltersComponentProps {
  filters: SalesFilters;
  onFiltersChange: (filters: SalesFilters) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  unities?: Array<{ id: number; name: string }>;
  clients?: Array<{ id: number; name: string }>;
  saleTypes?: Array<{ id: number; name: string }>;
  paymentForms?: Array<{ id: number; name: string }>;
  processStepSales?: Array<{ id: number; name: string }>;
  sellers?: Array<{ id: number; user: { name: string } }>;
  bankAccounts?: Array<{ id: number; name: string }>;
  lotteries?: Array<{ id: number; name: string }>;
  contests?: Array<{ id: number; name: string }>;
  clientOrigins?: Array<{ id: number; name: string }>;
  productClassifications?: Array<{ id: number; name: string }>;
  summaryMinTotal?: number | null;
  summaryMaxTotal?: number | null;
  /** Período inicial (padrão "today"). Use "all" pra não aplicar período. */
  defaultPeriod?: string;
  /** Mostra a opção "Todos" no seletor de período. */
  allowAllPeriod?: boolean;
  /** Esconde "Status da Venda" (não se aplica a outras telas, ex: pré-venda). */
  hideSaleStatus?: boolean;
  /** Esconde "Registros por página" (quando a tela já tem esse controle). */
  hidePerPage?: boolean;
  /** Campos extras da tela, renderizados junto dos demais filtros. */
  extraFilters?: ReactNode;
  /** Esconde filtros próprios da venda (tipo de cliente, produto, valor e horário) em telas que não os usam. */
  hideSaleSpecificFilters?: boolean;
}

export const SalesFiltersComponent = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  unities = [],
  clients = [],
  saleTypes = [],
  paymentForms = [],
  processStepSales = [],
  sellers = [],
  bankAccounts = [],
  lotteries = [],
  contests = [],
  clientOrigins = [],
  productClassifications = [],
  summaryMinTotal,
  summaryMaxTotal,
  defaultPeriod = "today",
  allowAllPeriod = false,
  hideSaleStatus = false,
  hidePerPage = false,
  extraFilters,
  hideSaleSpecificFilters = false,
}: SalesFiltersComponentProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const canSeeBleedPaymentForm = useCanSeeBleedPaymentForm();
  // "Dinheiro - Sangria" só para admin/dono e usuários de unidade com sangria; "Dinheiro" aparece para todos
  const visiblePaymentForms = paymentForms.filter(
    (form) => canSeeBleedPaymentForm || form.id !== PAYMENT_FORMS.MONEY_BLEED
  );
  const [datePeriod, setDatePeriod] = useState<string>(filters.date_period || defaultPeriod);

  const updateFilter = (key: keyof SalesFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined,
    });
  };

  const updateArrayFilter = (key: keyof SalesFilters, value: string, action: 'add' | 'remove') => {
    const currentValues = (filters[key] as string[] | undefined) || [];
    let newValues: string[];

    if (action === 'add') {
      if (!currentValues.includes(value)) {
        newValues = [...currentValues, value];
      } else {
        return;
      }
    } else {
      newValues = currentValues.filter(v => v !== value);
    }

    onFiltersChange({
      ...filters,
      [key]: newValues.length > 0 ? newValues : undefined,
    });
  };

  // Aplicar filtro de "hoje" por padrão na primeira montagem
  useEffect(() => {
    if (defaultPeriod !== "all" && !filters.date_period && !filters.start_date && !filters.end_date) {
      handleDatePeriodChange(defaultPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDatePeriodChange = (period: string) => {
    setDatePeriod(period);
    const today = new Date();
    // Usa data local em vez de toISOString() que converte para UTC
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    let start_date = "";
    let end_date = "";

    if (period === "today") {
      start_date = formatDate(today);
      end_date = formatDate(today);
    } else if (period === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      start_date = formatDate(yesterday);
      end_date = formatDate(yesterday);
    } else if (period === "thisWeek") {
      const startOfWeek = new Date(today);
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startOfWeek.setDate(today.getDate() - diff);
      start_date = formatDate(startOfWeek);
      end_date = formatDate(today);
    } else if (period === "thisMonth") {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      start_date = formatDate(startOfMonth);
      end_date = formatDate(today);
    } else if (period === "last7days") {
      const last7Days = new Date(today);
      last7Days.setDate(today.getDate() - 7);
      start_date = formatDate(last7Days);
      end_date = formatDate(today);
    } else if (period === "all") {
      start_date = "";
      end_date = "";
    }

    onFiltersChange({
      ...filters,
      date_period: period === "all" ? undefined : (period as "today" | "yesterday" | "thisWeek" | "thisMonth" | "last7days" | "custom"),
      start_date: period === "custom" ? filters.start_date : start_date || undefined,
      end_date: period === "custom" ? filters.end_date : end_date || undefined,
    });
  };

  useEffect(() => {
    if (filters.date_period) {
      setDatePeriod(filters.date_period);
    } else {
      setDatePeriod(defaultPeriod);
    }
  }, [filters.date_period]);

  const hasActiveFilters = Object.values(filters).some(value => value);

  return (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Filtros</CardTitle>
          <div className="flex gap-2">
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={onClearFilters}
                className="h-8"
              >
                <X className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-8"
            >
              {isExpanded ? "Ocultar" : "Expandir"}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="space-y-4">
          <form onSubmit={(e) => { e.preventDefault(); onApplyFilters(); }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sale_id">ID da Venda</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  id="sale_id"
                  placeholder="Buscar por ID"
                  value={filters.sale_id || ""}
                  onChange={(e) => updateFilter("sale_id", e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_name">Nome do Cliente</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  id="client_name"
                  placeholder="Buscar por nome"
                  value={filters.client_name || ""}
                  onChange={(e) => updateFilter("client_name", e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Período</Label>
              <Select
                value={datePeriod || defaultPeriod}
                onValueChange={handleDatePeriodChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o período" />
                </SelectTrigger>
                <SelectContent>
                  {allowAllPeriod && <SelectItem value="all">Todos</SelectItem>}
                  <SelectItem value="today">Hoje</SelectItem>
                  <SelectItem value="yesterday">Ontem</SelectItem>
                  <SelectItem value="thisWeek">Essa Semana</SelectItem>
                  <SelectItem value="thisMonth">Esse Mês</SelectItem>
                  <SelectItem value="last7days">Últimos 7 dias</SelectItem>
                  <SelectItem value="custom">Personalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {datePeriod === "custom" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="start_date">Data Inicial</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={filters.start_date || ""}
                    onChange={(e) => updateFilter("start_date", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_date">Data Final</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={filters.end_date || ""}
                    onChange={(e) => updateFilter("end_date", e.target.value)}
                  />
                </div>
              </>
            )}

            {unities.length > 0 && (
              <div className="space-y-2">
                <Label>Unidade</Label>
                <div className="space-y-2">
                  {filters.unity_id && filters.unity_id.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {filters.unity_id.map((unityId) => {
                        const unityLabel = unities.find(u => u.id.toString() === unityId)?.name;
                        return (
                          <Badge
                            key={unityId}
                            variant="secondary"
                            className="flex items-center gap-1"
                          >
                            {unityLabel}
                            <X
                              className="h-3 w-3 cursor-pointer hover:text-red-500"
                              onClick={() => updateArrayFilter("unity_id", unityId, "remove")}
                            />
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  <Select
                    onValueChange={(value) => {
                      if (value && value !== "all") {
                        updateArrayFilter("unity_id", value, "add");
                      }
                    }}
                    value=""
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a unidade" />
                    </SelectTrigger>
                    <SelectContent>
                      {unities.map((unity) => {
                        const unityIds = Array.isArray(filters.unity_id)
                          ? filters.unity_id.map(String)
                          : filters.unity_id
                          ? [String(filters.unity_id || "") || ""]
                          : [];
                        const isSelected = unityIds.includes(unity.id.toString());
                        return (
                          <SelectItem key={unity.id} value={unity.id.toString()} disabled={isSelected}>
                            {unity.name} {isSelected && "(Selecionado)"}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {clients.length > 0 && (
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select
                  value={filters.client_id || ""}
                  onValueChange={(value) => updateFilter("client_id", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id.toString()}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {saleTypes.length > 0 && (
              <div className="space-y-2">
                <Label>Tipo de Venda</Label>
                <Select
                  value={filters.sale_type_id || ""}
                  onValueChange={(value) => updateFilter("sale_type_id", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {saleTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id.toString()}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {paymentForms.length > 0 && (
              <div className="space-y-2">
                <Label>Forma de Pagamento</Label>
                <div className="space-y-2">
                  {filters.payment_form_id && filters.payment_form_id.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {filters.payment_form_id.map((formId) => {
                        const formLabel = paymentForms.find(f => f.id.toString() === formId)?.name;
                        return (
                          <Badge
                            key={formId}
                            variant="secondary"
                            className="flex items-center gap-1"
                          >
                            {formLabel}
                            <X
                              className="h-3 w-3 cursor-pointer hover:text-red-500"
                              onClick={() => updateArrayFilter("payment_form_id", formId, "remove")}
                            />
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  <Select
                    onValueChange={(value) => {
                      if (value && value !== "all") {
                        updateArrayFilter("payment_form_id", value, "add");
                      }
                    }}
                    value=""
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a forma" />
                    </SelectTrigger>
                    <SelectContent>
                      {visiblePaymentForms.map((form) => {
                        const isSelected = filters.payment_form_id?.includes(form.id.toString());
                        return (
                          <SelectItem key={form.id} value={form.id.toString()} disabled={isSelected}>
                            {form.name} {isSelected && "(Selecionado)"}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {lotteries.length > 0 && (
              <div className="space-y-2">
                <Label>Loteria</Label>
                <MultiSelectCheckbox
                  options={lotteries.map((lottery) => ({
                    value: lottery.id.toString(),
                    label: lottery.name,
                  }))}
                  selectedValues={filters.lottery_id || []}
                  onSelectionChange={(values) => {
                    onFiltersChange({
                      ...filters,
                      lottery_id: values.length > 0 ? values : undefined,
                    });
                  }}
                  placeholder="Selecione a loteria"
                  selectAllLabel="SELECIONAR TUDO"
                />
              </div>
            )}

            {contests.length > 0 && (
              <div className="space-y-2">
                <Label>Concurso</Label>
                <div className="space-y-2">
                  {filters.contest_id && filters.contest_id.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {filters.contest_id.map((contestId) => {
                        const contestLabel = contests.find(c => c.id.toString() === contestId)?.name;
                        return (
                          <Badge
                            key={contestId}
                            variant="secondary"
                            className="flex items-center gap-1"
                          >
                            {contestLabel}
                            <X
                              className="h-3 w-3 cursor-pointer hover:text-red-500"
                              onClick={() => updateArrayFilter("contest_id", contestId, "remove")}
                            />
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  <Select
                    onValueChange={(value) => {
                      if (value && value !== "all") {
                        updateArrayFilter("contest_id", value, "add");
                      }
                    }}
                    value=""
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o concurso" />
                    </SelectTrigger>
                    <SelectContent>
                      {contests.map((contest) => {
                        const isSelected = filters.contest_id?.includes(contest.id.toString());
                        return (
                          <SelectItem key={contest.id} value={contest.id.toString()} disabled={isSelected}>
                            {contest.name} {isSelected && "(Selecionado)"}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {clientOrigins.length > 0 && (
              <div className="space-y-2">
                <Label>Mídia/Origem</Label>
                <div className="space-y-2">
                  {filters.client_origin_id && filters.client_origin_id.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {filters.client_origin_id.map((originId) => {
                        const originLabel = clientOrigins.find(o => o.id.toString() === originId)?.name;
                        return (
                          <Badge
                            key={originId}
                            variant="secondary"
                            className="flex items-center gap-1"
                          >
                            {originLabel}
                            <X
                              className="h-3 w-3 cursor-pointer hover:text-red-500"
                              onClick={() => updateArrayFilter("client_origin_id", originId, "remove")}
                            />
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  <Select
                    onValueChange={(value) => {
                      if (value && value !== "all") {
                        updateArrayFilter("client_origin_id", value, "add");
                      }
                    }}
                    value=""
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a mídia" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientOrigins.map((origin) => {
                        const isSelected = filters.client_origin_id?.includes(origin.id.toString());
                        return (
                          <SelectItem key={origin.id} value={origin.id.toString()} disabled={isSelected}>
                            {origin.name} {isSelected && "(Selecionado)"}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {processStepSales.length > 0 && (
              <div className="space-y-2">
                <Label>Status do Processo</Label>
                <Select
                  value={filters.process_step_sale_id || ""}
                  onValueChange={(value) => updateFilter("process_step_sale_id", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {processStepSales.map((step) => (
                      <SelectItem key={step.id} value={step.id.toString()}>
                        {step.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {sellers.length > 0 && (
              <div className="space-y-2">
                <Label>Vendedor</Label>
                <div className="space-y-2">
                  {filters.seller_id && filters.seller_id.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {filters.seller_id.map((sellerId) => {
                        const sellerLabel = sellers.find(s => s.id.toString() === sellerId)?.user?.name;
                        return (
                          <Badge
                            key={sellerId}
                            variant="secondary"
                            className="flex items-center gap-1"
                          >
                            {sellerLabel}
                            <X
                              className="h-3 w-3 cursor-pointer hover:text-red-500"
                              onClick={() => updateArrayFilter("seller_id", sellerId, "remove")}
                            />
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  <Select
                    onValueChange={(value) => {
                      if (value && value !== "all") {
                        updateArrayFilter("seller_id", value, "add");
                      }
                    }}
                    value=""
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o vendedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {sellers.map((seller) => {
                        const isSelected = filters.seller_id?.includes(seller.id.toString());
                        return (
                          <SelectItem key={seller.id} value={seller.id.toString()} disabled={isSelected}>
                            {seller?.user?.name} {isSelected && "(Selecionado)"}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {bankAccounts.length > 0 && (
              <div className="space-y-2">
                <Label>Conta Bancária</Label>
                <Select
                  value={filters.bank_account_id || ""}
                  onValueChange={(value) => updateFilter("bank_account_id", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a conta" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {bankAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.id.toString()}>
                        {account.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!hidePerPage && (
            <div className="space-y-2">
              <Label>Registros por página</Label>
              <Select
                value={filters.per_page?.toString() || "15"}
                onValueChange={(value) => updateFilter("per_page", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}

            {!hideSaleStatus && (
            <div className="space-y-2">
              <Label>Status da Venda</Label>
              <div className="space-y-2">
                {filters.sale_status_id && filters.sale_status_id.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {filters.sale_status_id.map((statusId) => {
                      const statusLabel = SALE_STATUS_LABELS[Number(statusId)];
                      return (
                        <Badge
                          key={statusId}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {statusLabel}
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-red-500"
                            onClick={() => updateArrayFilter("sale_status_id", statusId, "remove")}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <Select
                  onValueChange={(value) => {
                    if (value && value !== "all") {
                      updateArrayFilter("sale_status_id", value, "add");
                    }
                  }}
                  value=""
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SALE_STATUS_LABELS).map(([value, label]) => {
                      const isSelected = filters.sale_status_id?.includes(value);
                      return (
                        <SelectItem key={value} value={value} disabled={isSelected}>
                          {label} {isSelected && "(Selecionado)"}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            )}

            {extraFilters}

            {!hideSaleSpecificFilters && (
            <>
            <div className="space-y-2">
              <Label>Tipo de Cliente</Label>
              <Select
                value={filters.is_new_client || ""}
                onValueChange={(value) => updateFilter("is_new_client", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="1">Novo</SelectItem>
                  <SelectItem value="0">Antigo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {productClassifications.length > 0 && (
              <div className="space-y-2">
                <Label>Tipo de Bolão</Label>
                <div className="space-y-2">
                  {filters.product_classification_id && filters.product_classification_id.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {filters.product_classification_id.map((classificationId) => {
                        const classificationLabel = productClassifications.find(p => p.id.toString() === classificationId)?.name;
                        return (
                          <Badge
                            key={classificationId}
                            variant="secondary"
                            className="flex items-center gap-1"
                          >
                            {classificationLabel}
                            <X
                              className="h-3 w-3 cursor-pointer hover:text-red-500"
                              onClick={() => updateArrayFilter("product_classification_id", classificationId, "remove")}
                            />
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  <Select
                    onValueChange={(value) => {
                      if (value && value !== "all") {
                        updateArrayFilter("product_classification_id", value, "add");
                      }
                    }}
                    value=""
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {productClassifications.map((classification) => {
                        const isSelected = filters.product_classification_id?.includes(classification.id.toString());
                        return (
                          <SelectItem key={classification.id} value={classification.id.toString()} disabled={isSelected}>
                            {classification.name} {isSelected && "(Selecionado)"}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="product_name">Nome/Número do Produto</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  id="product_name"
                  placeholder="Buscar por nome/número"
                  value={filters.product_name || ""}
                  onChange={(e) => updateFilter("product_name", e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="min_total">Valor Mínimo</Label>
              <Input
                id="min_total"
                typeMask="currency"
                placeholder={summaryMinTotal ? Mask.formatCurrency(summaryMinTotal) : "R$ 0,00"}
                value={formatNumberToCurrency(filters.min_total)}
                onChange={(e) => {
                  const value = parseCurrencyToNumber(e.target.value);
                  onFiltersChange({
                    ...filters,
                    min_total: value || undefined,
                  });
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="max_total">Valor Máximo</Label>
              <Input
                id="max_total"
                typeMask="currency"
                placeholder={summaryMaxTotal ? Mask.formatCurrency(summaryMaxTotal) : "R$ 0,00"}
                value={formatNumberToCurrency(filters.max_total)}
                onChange={(e) => {
                  const value = parseCurrencyToNumber(e.target.value);
                  onFiltersChange({
                    ...filters,
                    max_total: value || undefined,
                  });
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>Compra entre</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="start_time"
                  type="time"
                  value={filters.start_time || ""}
                  onChange={(e) => updateFilter("start_time", e.target.value)}
                  className="w-full"
                />
                <span className="text-sm text-gray-500">e</span>
                <Input
                  id="end_time"
                  type="time"
                  value={filters.end_time || ""}
                  onChange={(e) => updateFilter("end_time", e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
            </>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button
              type="submit"
              className={cn("gap-2", colors.bg_primary)}
            >
              <Search className="w-4 h-4" />
              Aplicar Filtros
            </Button>
          </div>
          </form>
        </CardContent>
      )}
    </Card>
  );
};